import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, CalendarClock } from 'lucide-react'
import type { Campaign, ScheduleItem } from '@/api/client'
import { TONE_CLASS, type Tone } from '@/lib/status'
import { formatShort } from '@/lib/time'
import { cn } from '@/lib/utils'
import { Button, EmptyState, ErrorState, SkeletonRows } from '@/ui'
import { PostLine } from '../schedule/PostLine'
import { Section, nextSlots, todayOf, upcomingOf } from './shared'

/** How many waiting posts the page lists before 일정에서 보기. */
const SHOWN_UPCOMING = 5

/** One figure in the status card: what it is, the figure, one line under it. */
function Figure({ label, value, hint, className }: { label: string; value: ReactNode; hint?: ReactNode; className?: string }) {
  return (
    <div className={cn('min-w-0', className)}>
      <div className="text-meta text-muted-foreground">{label}</div>
      <div className="mt-1 truncate text-title font-semibold tabular-nums sm:text-stat">{value}</div>
      {hint && <div className="mt-1.5 text-meta text-muted-foreground">{hint}</div>}
    </div>
  )
}

/** Today's posts against the day's limit: what went up, then what is booked. */
function DayBar({ published, booked, limit }: { published: number; booked: number; limit: number }) {
  const pct = (n: number) => `${Math.min(100, (n / limit) * 100)}%`
  return (
    <div aria-hidden className="mt-2 flex h-1.5 overflow-hidden rounded-full bg-muted">
      <span className={TONE_CLASS.published.dot} style={{ width: pct(published) }} />
      <span className={TONE_CLASS.scheduled.dot} style={{ width: pct(Math.max(0, Math.min(booked, limit - published))) }} />
    </div>
  )
}

/** One state count that opens its list. Zero is greyed; review and failure
 *  take their colour only when there is something to look at. */
function Count({ label, n, tone, to, sub }: { label: string; n: number; tone?: Tone; to: string; sub?: string }) {
  return (
    <Link to={to} className="min-w-0 rounded-md px-2 py-1.5 transition-colors hover:bg-muted">
      <span className="block truncate text-meta text-muted-foreground">{label}</span>
      <span className={cn('block text-section font-semibold tabular-nums', n === 0 ? 'text-muted-foreground/60' : tone && TONE_CLASS[tone].text)}>
        {n}
      </span>
      {sub && <span className="block truncate text-micro text-muted-foreground">{sub}</span>}
    </Link>
  )
}

/** 현황 — is it running and what goes out next: today against the day's
 *  limit, the next slot, who approves, and the posts by state. */
export function StatusCard({
  c,
  counts,
  feed,
  loading,
  now = new Date(),
}: {
  c: Campaign
  counts: Record<string, number>
  /** The campaign's posts from yesterday on, the waiting lane included. */
  feed: ScheduleItem[]
  loading: boolean
  now?: Date
}) {
  const num = (...keys: string[]) => keys.reduce((a, k) => a + (counts[k] ?? 0), 0)
  const query = `campaign_id=${c.ID}`
  const day = todayOf(feed, c, now)
  const used = day.published + day.booked
  const [next, after] = nextSlots(feed, now)
  const active = c.Status === 'active'
  const auto = c.Mode === 'affiliate' && !!c.Affiliate?.auto_approve
  const sending = num('publishing')

  return (
    <Section title="현황">
      <div className="grid grid-cols-2 gap-x-4 gap-y-4 sm:grid-cols-3">
        <div className="min-w-0">
          <Figure
            label="오늘"
            value={
              <>
                {loading ? '—' : used}
                <span className="text-section font-normal text-muted-foreground"> / {day.limit}건</span>
              </>
            }
          />
          <DayBar published={day.published} booked={day.booked} limit={day.limit} />
          <div className="mt-1.5 text-meta tabular-nums text-muted-foreground" data-testid="today">
            게시 {day.published} · 예약 {day.booked}
            {used < day.limit && ` · 남은 자리 ${day.limit - used}`}
          </div>
        </div>
        <Figure
          label="다음 게시"
          value={next ? formatShort(next.scheduled_at, now) : '—'}
          hint={!active && next ? '멈춰서 나가지 않는다' : next ? (after ? `그다음 ${formatShort(after.scheduled_at, now)}` : '그 뒤 예약 없음') : '잡힌 예약 없음'}
        />
        {/* On the phone one line under the two figures, not a third block. */}
        <div className="col-span-2 flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5 sm:col-span-1 sm:block">
          <div className="text-meta text-muted-foreground">승인</div>
          <div className="text-body font-semibold sm:mt-1 sm:text-stat">{auto ? '자동' : '직접'}</div>
          <div className="text-meta text-muted-foreground sm:mt-1.5">
            {auto ? `점수 높은 글을 하루 ${day.limit}건까지 예약한다` : '검토에서 승인한 글만 예약된다'}
          </div>
        </div>
      </div>
      <nav aria-label="글 현황" className="-mx-2 grid grid-cols-4 gap-1 border-t border-border pt-2">
        <Count label="검토 대기" n={num('pending_review')} tone="review" to={`/review?${query}`} />
        <Count label="예약" n={num('approved', 'scheduled')} to={`/schedule?${query}`} sub={sending > 0 ? `게시 중 ${sending}` : undefined} />
        <Count label="게시됨" n={num('published')} to={`/results?${query}`} />
        <Count label="실패" n={num('failed')} tone="failed" to={`/schedule?${query}&status=failed`} />
      </nav>
    </Section>
  )
}

/** 예정된 글 — what goes out next, in the order the campaign cards use:
 *  being sent, then by slot, then approved with no slot. A row opens the post. */
export function UpcomingPosts({
  campaignId,
  feed,
  loading,
  error,
  onRetry,
  onReslot,
}: {
  campaignId: number
  feed: ScheduleItem[]
  loading: boolean
  error: unknown
  onRetry: () => void
  /** Offered when posts are booked: move them to the slots today's rules give. */
  onReslot?: () => void
}) {
  const items = upcomingOf(feed)
  const rest = items.length - SHOWN_UPCOMING
  const to = `/schedule?campaign_id=${campaignId}`
  return (
    <Section
      title="예정된 글"
      aside={items.length > 0 ? `${items.length}건` : undefined}
      label="예정된 글"
      actions={
        <>
          {onReslot && (
            <Button size="sm" icon={CalendarClock} onClick={onReslot}>
              예약 다시 배치
            </Button>
          )}
          <Link to={to} className="inline-flex items-center gap-1 text-meta font-medium text-accent hover:underline">
            일정에서 보기
            <ArrowRight size={12} aria-hidden />
          </Link>
        </>
      }
    >
      {error != null ? (
        <ErrorState title="예정된 글을 불러오지 못했다" error={error} onRetry={onRetry} />
      ) : loading ? (
        <SkeletonRows rows={3} />
      ) : items.length === 0 ? (
        <EmptyState className="py-6" title="잡힌 예약이 없다" description="승인한 글은 페이싱 규칙대로 시각이 잡혀 여기에 나온다." />
      ) : (
        <>
          <ul className="divide-y divide-border overflow-hidden rounded-md border border-border">
            {items.slice(0, SHOWN_UPCOMING).map((it) => (
              <PostLine key={`${it.kind}-${it.id}`} it={it} />
            ))}
          </ul>
          {rest > 0 && (
            <Link to={to} className="inline-block text-meta text-muted-foreground hover:text-foreground hover:underline">
              {rest}건 더 — 일정에서 본다
            </Link>
          )}
        </>
      )}
    </Section>
  )
}
